import DOMPurify from 'dompurify';

import Tools from '../Tools/Tools';
import Validation from '../Validation/Validation';

import Select from './Select';

/**
 * Třída pro obsluhu velikostí banneru
 */
class SelectAndDisplay {
	/**
	 * Konstroktor, který nastaví proměnné a nastartuje select. Je nutné dodržení pořadí proměnných!
	 * @param {object} container - HTML element obsahující select a prostor na vytváření boxů
	 * @param {array} selectsHead - Nadpisy selectu pro 2 stavy: jsou ještě optiony a nejsou žádné další optiony. Tato proměnná se dědí
	 * @param {string} name - Název, který specifikuje tuto instanci (defaultně je nastaven na 'parent')
	 */
	constructor(container, selectsHead = ['Možnosti', 'Žádné další možnosti'], name = 'Unknown') {
		if (!container) {
			return;
		}

		this.container = container;
		this.selectsHead = selectsHead;
		this.name = name;

		this.children = [];
		this.chosenSelectOptions = [];
		this.zonesSubcategories = ['Sklik', 'IM', 'Pubmatic', 'Xandr'];
		this._initSelect();
	}

	/**
	 * Vrací vybrané options.
	 * @return {array}
	 */
	getSelectedOptions() {
		return this.chosenSelectOptions;
	}

	/**
	 * Tato funkce vytvoří a přiřadí nového childa.
	 * @param {object} container - Objekt obsahující HTML element selectu.
	 */
	setChildren(container, name) {
		if (!container) {
			return;
		}

		const child = new SelectAndDisplay(container, this.selectsHead, name);

		this.children.push(child);
	}

	/**
	 * Tato funkce inicializuje a rozpohybuje select pro výběr rozměrů baneru.
	 */
	_initSelect() {
		this.select = new Select(true);

		this.select.initSelectWithElement(this.container, this._choseSelectOptionEvent.bind(this));

		this.select.setCustomHeadlineOption(this.selectsHead[0]);
		this.options = this.select.getSelectedOption();
		this._setPreselectedOptions();
	}

	/**
	 * Pro případ editace formuláře, lze nastavit vybrané options, podle kterých se vygenerují boxy.
	 * Element .Select-option musí mít data-preselected="true".
	 */
	_setPreselectedOptions() {
		Array.from(this.container.querySelectorAll('.Select-option[data-preselected="true"]')).forEach((selected) => {
			const value = selected.getAttribute('value');
			const boxTitle = selected.textContent;
			const group = selected.getAttribute('data-group');

			this.choseSelectOption(value, boxTitle, group);
		});
	}

	/**
	 * Tato funkce vygeneruje do DOMu nový box.
	 * @param {string} value - Text/číslo, které bude uvnitř boxu.
	 * @param {string} boxTitle Text/číslo, které bude uvnitř boxu.
	 * @param {string} group Název sekce do které option spadá, pokud nejsou sekce, tak undefined.
	 */
	_generateBox(value, boxTitle, group) {
		const boxId = this.container.getAttribute('data-id');

		if (!this.boxesContainer) {
			this.boxesContainer = document.getElementById(boxId);
		}

		if (!this.boxesList) {
			this.boxesList = Tools.createElement('ul', {
				class: 'Form-selectedItems' + (boxId ? ' Form-selectedItems--' + boxId : ''),
			});

			this.boxesContainer.appendChild(this.boxesList);
		}
		const li = Tools.createElement('li', {
			'data-group': group ? group : '',
			'data-value': value,
			innerHTML: DOMPurify.sanitize(boxTitle),
		});

		Validation.hideInputError(this.select.getElement());
		this.boxesList.appendChild(li);
		li.addEventListener('click', this._removeChosenBoxEvent.bind(this));
	}

	/**
	 * Tato funkce obsluhuje událost při kliknutí na option v selectu.
	 * @param {string} selected Vybraná možnost.
	 */
	_choseSelectOptionEvent(selected, select) {
		const option = select.getSelectedOptionElem(selected);

		this.choseSelectOption(
			selected,
			option.textContent, // Titulek boxu.
			option.getAttribute('data-group'),
		);

		select.toggleSelect();
	}

	/**
	 * Tato funkce odstraní option v selectu a zároveň se vytvoří nový box.
	 * @param {string} value Hodnota boxu.
	 * @param {string} boxTitle Text/číslo, které bude uvnitř boxu.
	 * @param {string} group Název sekce do které option spadá, pokud nejsou sekce, tak undefined.
	 */
	choseSelectOption(value, boxTitle, group) {
		// Pokud boxík ješte neexistuje, tak ho vytvořím.
		if (!~this.chosenSelectOptions.indexOf(value)) {
			this._generateBox(value, boxTitle, group);
			this.options = this.select.removeOption(value);
			this.select.serviceHiddenInputs(value, null, false);
			this.chosenSelectOptions.push(value);
		}

		// Podle zbývajících dat nastavím text v tlačítku selectu.
		if (this.options && !this.options.length) {
			this.select.setCustomHeadlineOption(this.selectsHead[1]);
		} else {
			this.select.setCustomHeadlineOption(this.selectsHead[0]);
		}

		if (this.children.length > 0) {
			this.children.forEach((child) => child.choseSelectOption(value, boxTitle, group));
		}
	}

	/**
	 * Tato funkce obsluhuje událost při kliknutí na box.
	 * @param {object} e - Objekt obsahující událost.
	 */
	_removeChosenBoxEvent(e) {
		this._removeChosenBox(
			e.target.getAttribute('data-value'),
			e.target, // boxík element
		);
	}

	/**
	 * Tato funkce obsluhuje událost při kliknutí na box a odstraní jej. Zároveň se rozměr baneru vrátí do selectu.
	 * @param {string} value - Hodnota v boxu.
	 * @param {object} boxElement - box.
	 */
	_removeChosenBox(value, boxElement) {
		if (!boxElement) {
			return;
		}

		const index = this.chosenSelectOptions.indexOf(value);

		if (index > -1) {
			this.options = this.select.setNewOption([value, boxElement.textContent], boxElement.getAttribute('data-group'));

			this.boxesList.removeChild(boxElement);

			// Odstraňuji z domu, protože tam vytváří odsazení..
			if (!this.boxesList.children.length) {
				Tools.removeElement(this.boxesList);
				this.boxesContainer = null;
				this.boxesList = null;
				Validation.showInputError(this.select.getElement());
			}

			this.select.setCustomHeadlineOption(this.selectsHead[0]);
			this.select.serviceHiddenInputs(value, null, false);
			this.chosenSelectOptions.splice(index, 1);
		}

		this.children.forEach((child) => {
			if (child.boxesContainer) {
				child._removeChosenBox(value, child.boxesContainer.querySelector(`li[data-value="${value}"]`));
			}

			// Pokud odeberu rozměr v rodiči, tak se odstraní i ze selectu potomka.
			if (this.name === 'Common') {
				child.select.removeOption(value);
				child.select.setCustomHeadlineOption(this.selectsHead[0]);
			}
		});

		// Pokud nejsou vybrané žádné rozměry, tak sekce je neaktivní.
		if (~this.zonesSubcategories.indexOf(this.name)) {
			if (!this.chosenSelectOptions.length) {
				document.querySelector(
					// eslint-disable-next-line typescript-eslint/restrict-template-expressions
					`.TabNav-item--${this.name} input`,
				).checked = false;
			}
		}
	}

	/**
	 * Resetuje options selectu.
	 * @param {array} options
	 */
	resetWithNewOptions(options) {
		// Odstranění boxíků.
		if (this.boxesContainer) {
			this.boxesContainer.innerHTML = '';
			this.boxesList = null;
		}

		this.chosenSelectOptions = [];
		this.select.setNewOptions(options);
		this.select.setCustomHeadlineOption(this.selectsHead[0]);
		this.select.removeHiddenInputs();
	}
}

export default SelectAndDisplay;
